import React from "react";
import { Plus, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { StepCard, Field, ToggleRow, SCALE_OPTIONS } from "./_helpers";

/** Step 0 — Grading System Settings + Scale Ranges table. */
export default function Step0Settings({
  settings, onSettingsChange,
  scaleRanges, onAddScaleRow, onUpdScaleRow, onDelScaleRow,
}) {
  const isCustom = settings.grading_scale_type === "custom";
  const showScaleRanges = settings.grading_scale_type !== "percentage";

  return (
    <StepCard step="Step 0" title="Grading System Settings"
      subtitle="Choose how grades will be encoded, calculated, and displayed."
      testId="step-0"
    >
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="GRADING MODE">
          <select value={settings.grading_mode}
            onChange={(e) => onSettingsChange({ grading_mode: e.target.value })}
            className="gs-select" data-testid="gs-grading-mode">
            <option value="calculated">Calculated Grading</option>
            <option value="direct">Direct Assessment</option>
          </select>
        </Field>
        <Field label="GRADING SCALE / SYSTEM">
          <select value={settings.grading_scale_type}
            onChange={(e) => onSettingsChange({ grading_scale_type: e.target.value })}
            className="gs-select" data-testid="gs-scale-type">
            {SCALE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </Field>
      </div>

      {settings.grading_mode === "direct" && (
        <Field label="DIRECT ASSESSMENT LEVEL" className="mt-3">
          <select value={settings.direct_assessment_level || "category"}
            onChange={(e) => onSettingsChange({ direct_assessment_level: e.target.value })}
            className="gs-select" data-testid="gs-direct-level">
            <option value="category">Category Level</option>
            <option value="component">Component Level</option>
            <option value="task">Task Level</option>
          </select>
        </Field>
      )}

      <ToggleRow
        label="Allow Comments per Grading Period"
        description="When enabled, teachers can write a comment per period for each student. Shown on the printed report."
        checked={settings.allow_grading_term_comments}
        onChange={(v) => onSettingsChange({ allow_grading_term_comments: v })}
        testId="gs-allow-comments"
      />

      {isCustom && (
        <div className="grid sm:grid-cols-2 gap-3 mt-3">
          <ToggleRow
            label="Enable Description Field"
            description="Show a description column for each custom scale row."
            checked={settings.custom_description_enabled}
            onChange={(v) => onSettingsChange({ custom_description_enabled: v })}
            testId="gs-custom-desc"
            flat
          />
          <ToggleRow
            label="Enable Percentage Range"
            description="Map each custom code to a min/max percentage range."
            checked={settings.custom_percentage_range_enabled}
            onChange={(v) => onSettingsChange({ custom_percentage_range_enabled: v })}
            testId="gs-custom-range"
            flat
          />
        </div>
      )}

      {showScaleRanges && (
        <div className="mt-4 border-t border-brand-purple/10 pt-4" data-testid="gs-scale-ranges">
          <div className="flex items-center justify-between mb-2">
            <p className="font-heading text-sm font-semibold text-foreground">Scale Ranges</p>
            <Button size="sm" variant="outline" onClick={onAddScaleRow} className="rounded-xl" data-testid="gs-add-scale">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add Row
            </Button>
          </div>
          {scaleRanges.length === 0 ? (
            <p className="text-xs text-brand-inkMuted py-4 text-center bg-brand-purpleLight/30 rounded-xl">No ranges yet. Click "Add Row" to define one.</p>
          ) : (
            <div className="space-y-2">
              {scaleRanges.map((r) => (
                <div key={r.id} className="grid grid-cols-12 gap-2 items-center" data-testid={`gs-scale-row-${r.id}`}>
                  <Input value={r.code_or_value} onChange={(e) => onUpdScaleRow(r.id, { code_or_value: e.target.value })}
                    placeholder="Code (e.g. A)" className="col-span-3 rounded-xl h-10" />
                  {(isCustom ? settings.custom_description_enabled : true) && (
                    <Input value={r.description || ""} onChange={(e) => onUpdScaleRow(r.id, { description: e.target.value })}
                      placeholder="Description" className="col-span-4 rounded-xl h-10" />
                  )}
                  {(!isCustom || settings.custom_percentage_range_enabled) && (
                    <>
                      <Input type="number" value={r.min_percentage ?? ""} onChange={(e) => onUpdScaleRow(r.id, { min_percentage: Number(e.target.value) })}
                        placeholder="Min %" className="col-span-2 rounded-xl h-10" />
                      <Input type="number" value={r.max_percentage ?? ""} onChange={(e) => onUpdScaleRow(r.id, { max_percentage: Number(e.target.value) })}
                        placeholder="Max %" className="col-span-2 rounded-xl h-10" />
                    </>
                  )}
                  <Button size="icon" variant="ghost" onClick={() => onDelScaleRow(r.id)} className="col-span-1 text-[#C62828]" data-testid={`gs-del-scale-${r.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Report Card Signatories --------------------------------------------
          Names + titles printed at the bottom of every report card in this
          scope. Single signatory → centered. Two or more → left / right (…).
      */}
      <div className="mt-4 border-t border-brand-purple/10 pt-4" data-testid="gs-signatories">
        <div className="flex items-center justify-between mb-2">
          <div>
            <p className="font-heading text-sm font-semibold text-foreground">Report Card Signatories</p>
            <p className="text-xs text-brand-inkMuted">Names & titles printed at the bottom of the printed report card.</p>
          </div>
          <Button size="sm" variant="outline"
            onClick={() => onSettingsChange({ report_card_signatories: [
              ...(settings.report_card_signatories || []), { name: "", title: "" },
            ] })}
            className="rounded-xl" data-testid="gs-add-signatory">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add Signatory
          </Button>
        </div>
        {(!settings.report_card_signatories?.length) ? (
          <p className="text-xs text-brand-inkMuted py-4 text-center bg-brand-purpleLight/30 rounded-xl">
            No signatories yet. Click "Add Signatory" to add one (e.g. Principal, Adviser).
          </p>
        ) : (
          <div className="space-y-2">
            {(settings.report_card_signatories || []).map((s, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`gs-sig-row-${idx}`}>
                <Input
                  value={s.name || ""}
                  onChange={(e) => {
                    const arr = [...(settings.report_card_signatories || [])];
                    arr[idx] = { ...arr[idx], name: e.target.value };
                    onSettingsChange({ report_card_signatories: arr });
                  }}
                  placeholder="Full Name (e.g. Juan Dela Cruz)"
                  className="col-span-5 rounded-xl h-10"
                  data-testid={`gs-sig-name-${idx}`}
                />
                <Input
                  value={s.title || ""}
                  onChange={(e) => {
                    const arr = [...(settings.report_card_signatories || [])];
                    arr[idx] = { ...arr[idx], title: e.target.value };
                    onSettingsChange({ report_card_signatories: arr });
                  }}
                  placeholder="Title / Rank (e.g. Principal)"
                  className="col-span-6 rounded-xl h-10"
                  data-testid={`gs-sig-title-${idx}`}
                />
                <Button
                  size="icon" variant="ghost"
                  onClick={() => {
                    const arr = [...(settings.report_card_signatories || [])];
                    arr.splice(idx, 1);
                    onSettingsChange({ report_card_signatories: arr });
                  }}
                  className="col-span-1 text-[#C62828]"
                  data-testid={`gs-del-sig-${idx}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </StepCard>
  );
}
